import { Box, Stack } from "@mui/material";
import type { BaseSyntheticEvent, MouseEvent } from "react";
import { useState } from "react";
import BadgeDetached from "./BadgeDetached";
import ItemsListPopover from "./ItemsListPopover";
import StatusChip from "./StatusChip";

interface TagInfo {
  label: string;
  color: string;
}

interface Props {
  tags: TagInfo[];
  maxDisplayedTags?: number;
  noWrap?: boolean;
}

const InlineTags = ({ tags, maxDisplayedTags, noWrap }: Props) => {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | undefined>();

  const displayedTags = tags.slice(0, maxDisplayedTags);
  const hiddenTags = maxDisplayedTags === undefined ? [] : tags.slice(maxDisplayedTags);
  const hasHiddenTags = hiddenTags.length > 0;
  const inlineTags = hasHiddenTags ? displayedTags.slice(0, -1) : displayedTags;

  const lastDisplayedTag = hasHiddenTags ? displayedTags[displayedTags.length - 1] : undefined;

  const handleBadgeClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setPopoverAnchorEl((prev) => (prev ? undefined : event.currentTarget));
  };

  const handlePopoverClose = (event: BaseSyntheticEvent) => {
    event.stopPropagation();
    setPopoverAnchorEl(undefined);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: noWrap ? "nowrap" : "wrap" }}>
      {inlineTags.map((tag) => (
        <StatusChip key={tag.label} label={tag.label} color={tag.color} />
      ))}

      {hasHiddenTags && (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", maxWidth: "100%" }}>
          {lastDisplayedTag && <StatusChip label={lastDisplayedTag.label} color={lastDisplayedTag.color} />}

          <BadgeDetached
            color="secondary"
            badgeContent={`+${hiddenTags.length}`}
            onClick={handleBadgeClick}
            sx={{ cursor: "pointer" }}
          />

          <ItemsListPopover anchorEl={popoverAnchorEl} onClose={handlePopoverClose}>
            <Stack spacing={1} sx={{ maxWidth: "25rem", alignItems: "flex-start" }}>
              {hiddenTags.map((tag) => (
                <StatusChip key={tag.label} label={tag.label} color={tag.color} />
              ))}
            </Stack>
          </ItemsListPopover>
        </Stack>
      )}
    </Box>
  );
};

export default InlineTags;
